import {
  ActionButton,
  Button,
  ButtonGroup,
  Checkbox,
  Content,
  Dialog,
  Divider,
  Flex,
  Footer,
  Heading,
  Link,
  Text,
  TextArea,
  TextField,
  View,
  useDialogContainer,
} from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import { success } from '@react/react-spectrum/Toast';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import React, { useEffect, useMemo, useState } from 'react';
import { useVisuallyHidden } from 'react-aria';
import styled from 'styled-components';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { useIMS } from '@exchange-frontends/custom-hooks';

export const MESSAGES = {
  company: {
    empty: 'Enter your company name.',
    maxLength: 'Invalid format: this field accepts a max number of 100 characters.',
  },
  message: {
    empty: 'Enter a message to publisher.',
    maxLength: 'Invalid format: this field accepts a max number of 1000 characters',
  },
  terms: {
    empty: 'Select the checkbox to continue.',
  },
};

const getInitialFormValues = () => ({
  email: { value: '', isRequired: true, id: 'email' },
  name: { value: '', isRequired: true, id: 'name' },
  company: { value: '', isRequired: true, id: 'company', maxLength: 100 },
  message: { value: '', isRequired: true, id: 'message', maxLength: 1000 },
  terms: { value: false, isRequired: true, id: 'terms' },
});

const FormContainer = styled.div`
  margin-top: 30px;
  &&&& > div {
    display: ${({ $isTabletSize }) => ($isTabletSize ? 'flex' : 'grid')};
    grid-template-columns: 1fr 3fr;
    margin: 15px 0;
    width: 100%;
    & > div {
      width: 100%;
    }
  }
`;

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RedText = styled.span`
  color: red;
`;

const ContactPublisherDialog = ({ appId, title, description, close, onGoBack, isSupportRequest }) => {
  const dialogTitle = title || 'Contact support team';
  const dialogContent =
    description ||
    'Fill out the form below to submit a support request for this solution. For Exchange related support reach out to our support team';
  const dialog = useDialogContainer();
  const { visuallyHiddenProps } = useVisuallyHidden();

  const [areTermsOpen, setAreTermsOpen] = useState(false);
  const [sendingEmailErrored, setSendingEmailErrored] = useState(false);
  const [sendingEmailLoading, setSendingEmailLoading] = useState(false);
  const [form, setForm] = useState(getInitialFormValues);
  const { matchedBreakpoints } = useBreakpoint();
  const ims = useIMS();
  const profileLoading = useMemo(() => ims?.isMock || !ims?.isSignedInUser(), [ims]);
  const isTabletSize = !matchedBreakpoints.includes('S');
  const labelPosition = isTabletSize ? 'top' : 'side';

  const validateField = (key, value) => {
    if (form[key].isRequired && !value) {
      return { valid: 'invalid', errorMessage: MESSAGES[key]?.empty };
    } else if (value?.length > form[key]?.maxLength) {
      return { valid: 'invalid', errorMessage: MESSAGES[key]?.maxLength };
    } else if (form[key].isRequired) {
      return { valid: 'valid' };
    } else {
      return {};
    }
  };

  const validateForm = () => {
    let result = true;
    const newForm = { ...form };
    // const errors = [];

    Object.keys(form).forEach((key) => {
      const { valid, errorMessage } = validateField(key, form[key].value);
      newForm[key].valid = valid;
      newForm[key].errorMessage = errorMessage;
      if (valid === 'invalid') {
        result = false;
      }
    });

    setForm(newForm);
    return result;
  };

  const closeDialog = () => {
    close?.();
    dialog.dismiss?.();
  };

  const onFormChange = (key, value) => {
    setForm((prev) => {
      const newState = { ...prev };
      const { valid, errorMessage } = validateField(key, value);
      newState[key].value = value;
      newState[key].valid = valid;
      newState[key].errorMessage = errorMessage;
      return newState;
    });
  };

  // pre-fill email/name with ims profile
  // otherwise redirect to sign in
  useEffect(() => {
    let mounted = true;
    const setFormValues = async () => {
      if (!ims?.isMock) {
        if (ims?.isSignedInUser()) {
          const { email, first_name, last_name } = await ims.getProfile();
          if (mounted) {
            setForm((prev) => {
              const newState = { ...prev };
              newState.email.value = email;
              newState.name.value = `${first_name} ${last_name}`;
              return newState;
            });
          }
        } else {
          ims?.signIn();
        }
      }
    };
    setFormValues();
    return () => {
      mounted = false;
    };
  }, [ims]);

  const sendEmailToPublisher = async () => {
    if (validateForm()) {
      setSendingEmailLoading(true);
      setSendingEmailErrored(false);
      try {
        const accessToken = ims?.getAccessToken()?.token;
        const handler = handleApiCall(excApi.sendEmailToPublisher, {
          accessToken,
          company: form.company.value.trim(),
          message: form.message.value.trim(),
          isSupportRequest,
          appId,
        });
        await handler.run();
        success('Email was successfully sent to the publisher.');
        closeDialog();
      } catch (err) {
        console.log(err);
        setSendingEmailErrored(true);
      } finally {
        setSendingEmailLoading(false);
      }
    }
  };

  return (
    <Dialog>
      <Heading>{dialogTitle}</Heading>
      <Divider />
      <ButtonGroup>
        {onGoBack && (
          <Button variant="secondary" onPress={onGoBack}>
            Back
          </Button>
        )}
        <Button variant="secondary" onPress={closeDialog}>
          Cancel
        </Button>
        <Button
          autoFocus
          variant="cta"
          onPress={sendEmailToPublisher}
          isDisabled={profileLoading || sendingEmailLoading}
          isPending={sendingEmailLoading}
        >
          <Text>Submit</Text>
        </Button>
      </ButtonGroup>
      <Content>
        <Text>
          {dialogContent}{' '}
          <Link>
            <a href="https://partners.adobe.com/ec/cform/case" target="_blank" rel="noopener noreferrer">
              here
            </a>
          </Link>
          .)
        </Text>
        <FormContainer $isTabletSize={isTabletSize}>
          <TextField
            label="Your email"
            labelPosition={labelPosition}
            isRequired={form.email.isRequired}
            required
            type="email"
            id="email"
            value={form.email.value}
            isDisabled={profileLoading}
            isReadOnly
          />
          <TextField
            label="Your name"
            labelPosition={labelPosition}
            isRequired={form.name.isRequired}
            type="text"
            id="name"
            value={form.name.value}
            isDisabled={profileLoading}
            isReadOnly
          />
          <TextField
            errorMessage={form.company.errorMessage}
            validationState={form.company.valid}
            label="Company"
            labelPosition={labelPosition}
            isRequired={form.company.isRequired}
            type="text"
            id="company"
            value={form.company.value}
            isDisabled={profileLoading}
            autoComplete="organization"
            onChange={(value) => onFormChange(form.company.id, value)}
          />
          <div role="alert" aria-live="polite" {...visuallyHiddenProps}>
            {form.company.errorMessage}
          </div>
          <TextArea
            errorMessage={form.message.errorMessage}
            validationState={form.message.valid}
            label="Message to publisher"
            labelPosition={labelPosition}
            isRequired={form.message.isRequired}
            type="text"
            id="message"
            value={form.message.value}
            isDisabled={profileLoading}
            onChange={(value) => onFormChange(form.message.id, value)}
          />
          <div role="alert" aria-live="polite" {...visuallyHiddenProps}>
            {form.message.errorMessage}
          </div>
        </FormContainer>
        <TermsContainer>
          <Flex alignItems="center">
            <Checkbox
              id="terms"
              isRequired={form.terms.isRequired}
              value={form.terms.value}
              isInvalid={form.terms.valid === 'invalid'}
              aria-describedby="terms-checkbox-error"
              onChange={(value) => onFormChange(form.terms.id, value)}
              isDisabled={profileLoading}
            >
              By selecting this, you agree to Adobe Terms
            </Checkbox>
            <ActionButton
              isQuiet
              maxHeight="24px"
              maxWidth="24px"
              marginStart="2px"
              onPress={() => setAreTermsOpen((prev) => !prev)}
              aria-label="Adobe terms"
              aria-expanded={areTermsOpen}
              aria-controls="adobe-terms-content"
            >
              {areTermsOpen ? <ChevronUp /> : <ChevronDown />}
            </ActionButton>
          </Flex>
          <span
            id="terms-checkbox-error"
            role="alert"
            aria-live="polite"
            style={{ color: 'var(--spectrum-global-color-red-600)', fontSize: '12px' }}
          >
            {form.terms.valid === 'invalid' && MESSAGES.terms.empty}
          </span>
        </TermsContainer>
        {areTermsOpen && (
          <View
            id="adobe-terms-content"
            backgroundColor="gray-200"
            paddingX="size-200"
            paddingY="size-100"
            borderRadius="medium"
          >
            <Text>
              &ensp;I understand and agree that Partner and/or Adobe may contact me about products, services, and
              special offers. I understand and agree that any information collected by Adobe and Adobe&apos;s use of
              such information will be done subject to Adobe&apos;s Privacy Policy, and that any of my information
              provided to Partner will be subject to Partner&apos;s privacy policy and practices. Adobe shall not be
              responsible for Partner use of my information. If you would like to opt out of receiving materials related
              to Partner products, services, and special offerings from the Adobe, or have your information removed from
              use by Adobe, then please submit your opt out request via this{' '}
              <Link>
                <a href="https://partners.adobe.com/ec/cform/case" target="_blank" rel="noopener noreferrer">
                  page
                </a>
              </Link>{' '}
              and caption subject line &quot;Marketplace Opt-Out Request for [INSERT YOUR COMPANY NAME]&quot;.
            </Text>
          </View>
        )}
      </Content>
      <Footer>{sendingEmailErrored && <RedText>Failed to send email to publisher</RedText>}</Footer>
    </Dialog>
  );
};
export default ContactPublisherDialog;
